import moment from 'moment';

import {
  CaregiverRelationship,
  MemberContactMethodType,
  MemberContactTimeType,
  ProgramExtensionStatusReason,
} from '@vestahealthcare/common/enums';
import {
  MemberContactInfoTimes,
  MemberPhone,
  MemberProgramExtension,
  Patient,
  ProgramExtension,
  ProgramExtensionStatus,
} from '@vestahealthcare/common/models';
import { makeMoment, makeUnix } from '@vestahealthcare/common/models/Model';
import { getDiff } from '@vestahealthcare/common/utils/api';
import { DATE_FORMAT } from '@vestahealthcare/common/utils/constants';

import { CheckboxItem } from 'styleguide-v2/src/components/CheckboxWithLabel/CheckboxGroup';

import { DatesTimeItem } from 'dash/src/components/EditDateTimes';
import {
  MemberContactInfoParams,
  MemberContactInfoParsedResponse,
  MemberPhoneInvalidateParam,
  PatientParam,
  ProgramExtensionBulkParams,
} from 'dash/src/services/PatientServices';

export const getOtherContactType = (contactTypes: CheckboxItem[]) =>
  contactTypes.find(
    ({ value }) => value === MemberContactMethodType.OTHER.value,
  );

export const buildContactInfoParams = (
  contactTypes: CheckboxItem[],
  contactTypesOther: string,
  contactDateTimes: DatesTimeItem[],
  contactNote: string,
): MemberContactInfoParams => {
  const otherType = getOtherContactType(contactTypes);
  let contactMethods = [];
  if (otherType?.checked) {
    contactMethods = [
      {
        contactMethod: MemberContactMethodType.OTHER,
        contactMethodOther: contactTypesOther,
      },
    ];
  } else {
    contactMethods = contactTypes
      .filter(({ checked, disabled }) => checked && !disabled)
      .map(({ value }) => ({
        contactMethod: MemberContactMethodType.byKey[value],
      }));
  }

  const contactTimes: MemberContactInfoTimes[] = [];
  contactDateTimes.forEach(({ dates, type, startTime, endTime }) => {
    if (!dates?.length) {
      if (type) {
        contactTimes.push({
          type,
          startTime:
            type === MemberContactTimeType.CUSTOM && startTime
              ? moment(startTime.getTime())
              : undefined,
          endTime:
            type === MemberContactTimeType.CUSTOM && endTime
              ? moment(endTime.getTime())
              : undefined,
        } as MemberContactInfoTimes);
      }
    } else {
      dates.forEach((day) => {
        contactTimes.push({
          dayOfWeek: day,
          type: type || MemberContactTimeType.ANY_TIME,
          startTime:
            type === MemberContactTimeType.CUSTOM && startTime
              ? moment(startTime.getTime())
              : undefined,
          endTime:
            type === MemberContactTimeType.CUSTOM && endTime
              ? moment(endTime.getTime())
              : undefined,
        } as MemberContactInfoTimes);
      });
    }
  });

  return {
    contactMethods,
    contactTimes,
    note: contactNote,
  };
};

export const buildProgramExtensionParams = (
  programExtensions: MemberProgramExtension[],
) =>
  programExtensions.map(
    ({
      programExtension,
      releaseDate,
      serviceProvider,
      status,
      statusReason,
      statusReasonOther,
    }) => ({
      programExtension,
      status,
      releaseDate:
        status?.id === ProgramExtensionStatus.HOLD && releaseDate
          ? moment(releaseDate, DATE_FORMAT)?.format(DATE_FORMAT)
          : undefined,
      serviceProvider:
        programExtension?.id === ProgramExtension.CCM
          ? serviceProvider?.value || null
          : undefined,
      statusReasonOther:
        status?.id === ProgramExtensionStatus.HOLD &&
        statusReason === ProgramExtensionStatusReason.OTHER
          ? statusReasonOther
          : undefined,
      statusReason:
        status?.id === ProgramExtensionStatus.HOLD
          ? statusReason?.value
          : undefined,
    }),
  );

const CONTACT_SORT_TIME_ORDER = [
  undefined,
  MemberContactTimeType.ANY_TIME,
  MemberContactTimeType.MORNING,
  MemberContactTimeType.AFTERNOON,
  MemberContactTimeType.CUSTOM,
];

export const getDataFromContactInfo = (
  contactInfo: MemberContactInfoParsedResponse,
) => {
  const other = contactInfo.contactMethods?.find(
    ({ contactMethod }) => contactMethod === MemberContactMethodType.OTHER,
  );
  const contactTypes = MemberContactMethodType.toSelectable().map(
    ({ value, label }) =>
      ({
        checked: contactInfo.contactMethods?.find(
          ({ contactMethod }) => contactMethod?.value === value,
        ),
        disabled: other && value !== MemberContactMethodType.OTHER.value,
        value,
        label,
      } as CheckboxItem),
  );
  const contactTimes = contactInfo.contactDateTimes
    ?.filter(Boolean)
    ?.toSorted(
      (a, b) =>
        CONTACT_SORT_TIME_ORDER.indexOf(a?.type) -
        CONTACT_SORT_TIME_ORDER.indexOf(b?.type),
    )
    ?.map((item) => ({
      ...item,
      dates: [...(item.dates || [])],
    }));

  return {
    contactTypes,
    contactTimes,
    contactTypesOther: other?.contactMethodOther || '',
    note: contactInfo.note,
  };
};

export const getContactInfoDiff = (
  contactInfo?: MemberContactInfoParsedResponse,
  params?: MemberContactInfoParams,
) => {
  if (!contactInfo || !params) return false;

  const {
    contactTimes,
    contactTypes,
    contactTypesOther,
    note,
  } = getDataFromContactInfo(contactInfo);

  return !!getDiff<{ c: MemberContactInfoParams }>(
    {
      c: buildContactInfoParams(
        contactTypes,
        contactTypesOther,
        contactTimes,
        note,
      ),
    },
    { c: params || [] },
  ).c;
};

export const getMemberDetailDiff = (
  member?: Patient,
  params?: PatientParam,
): Partial<PatientParam> => {
  if (!member || !params) return {};

  const {
    address,
    birthDate,
    firstName,
    gender,
    genderComment,
    language,
    languages,
    lastName,
    livingArrangement,
    livingArrangementOther,
    residenceType,
    residenceTypeOther,
    selfDirecting,
  } = member;

  return getDiff<PatientParam>(
    {
      address: {
        ...address,
        state: address?.state?.value,
      },
      birthDate: makeMoment(birthDate),
      firstName,
      gender: {
        type: gender?.value,
        comment: genderComment,
      },
      language: language?.value,
      languages: languages?.map(({ value }) => value),
      lastName,
      livingArrangement: livingArrangement?.map(({ value }) => value),
      livingArrangementOther,
      residenceType: residenceType?.value ?? null,
      residenceTypeOther,
      selfDirecting: selfDirecting ?? null,
    },
    params,
  );
};

const checkPhoneEqual = (
  firstPhone: MemberPhone | MemberPhoneInvalidateParam,
) => (secondPhone: MemberPhone) =>
  firstPhone.id === secondPhone.id &&
  firstPhone.phone.number === secondPhone.phone.number &&
  firstPhone.phone.type === secondPhone.phone.type &&
  firstPhone.preferred === secondPhone.preferred &&
  firstPhone.primary === secondPhone.primary &&
  (
    (firstPhone as MemberPhoneInvalidateParam).reason ||
    firstPhone.inactivatedReason
  )?.value === secondPhone.inactivatedReason?.value &&
  ((firstPhone as MemberPhoneInvalidateParam).reasonOther ||
    firstPhone.inactivatedReasonOther) === secondPhone.inactivatedReasonOther;

export const getMemberPhonesDiff = (
  memberPhones: MemberPhone[] = [],
  activePhonesParams: MemberPhone[] = [],
  inactivatedPhonesParams: MemberPhoneInvalidateParam[] = [],
): boolean => {
  if (
    memberPhones.length !==
    activePhonesParams.length + inactivatedPhonesParams.length
  )
    return true;

  return (
    activePhonesParams.some(
      (activePhone) => !memberPhones.find(checkPhoneEqual(activePhone)),
    ) ||
    inactivatedPhonesParams.some(
      (inactivePhone) => !memberPhones.find(checkPhoneEqual(inactivePhone)),
    )
  );
};

export const getMemberProgramDiff = (
  member?: Patient,
  params?: PatientParam,
): Partial<PatientParam> => {
  if (!member || !params) return {};

  const {
    brand,
    deceasedDate,
    test,
    reasonWithdraw,
    reportingStartDate,
    status,
    hciNotifications,
    hciReporter,
    hciReporterMethod,
    appUser,
    virtualVisitDate,
    virtualVisitHasTime,
    virtualVisitHostName,
    virtualVisitHostPhoneNumber,
    virtualVisitHostRelationship,
    virtualVisitHostRelationshipOther,
    virtualVisitPlatform,
    virtualVisitStatus,
    worklistGroup,
    riskLevel,
  } = member;

  return getDiff<PatientParam>(
    {
      brandId: brand?.value,
      deceasedDate: makeMoment(deceasedDate),
      isTest: test,
      reasonWithdraw,
      reportingStartDate: makeMoment(reportingStartDate),
      status: status?.value,

      /** SELF REPORTER */
      hciNotifications,
      hciReporter,
      hciReporterMethod: hciReporter ? hciReporterMethod || null : null,
      authenticationPhone:
        hciReporter &&
        hciReporterMethod === MemberContactMethodType.MOBILE_APP_CHAT
          ? appUser?.authenticationPhone?.number || null
          : null,

      /** VIRTUAL VISIT */
      virtualVisitDate: virtualVisitDate ? makeUnix(virtualVisitDate) : null,
      virtualVisitPlatform: virtualVisitPlatform?.value || null,
      virtualVisitStatus: virtualVisitStatus?.value || null,
      virtualVisitHostName,
      virtualVisitHostRelationship: virtualVisitHostRelationship?.value || null,
      virtualVisitHostRelationshipOther:
        virtualVisitHostRelationship === CaregiverRelationship.OTHER
          ? virtualVisitHostRelationshipOther
          : null,
      virtualVisitHostPhoneNumber: virtualVisitHostPhoneNumber?.toString(),
      virtualVisitHasTime:
        typeof virtualVisitHasTime === 'boolean'
          ? virtualVisitHasTime
          : undefined,
      worklistGroupId: worklistGroup?.id,
      riskLevel: riskLevel?.value || null,
    },
    params,
  );
};

export const getProgramExtensionDiff = (
  programExtensions?: MemberProgramExtension[],
  params?: ProgramExtensionBulkParams[],
) => {
  if (!programExtensions || !params) return false;

  return !!getDiff<{ c: ProgramExtensionBulkParams[] }>(
    {
      c: buildProgramExtensionParams(programExtensions),
    },
    { c: params || [] },
  ).c;
};
